import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { Loader2, Hash, Eye, Flame, ArrowLeft, Play } from "lucide-react";
import { toast } from "sonner";

/**
 * Plyx Hashtag landing page — /plyx/tag/:slug
 * Shows lifetime + 7-day stats, a grid of top videos using the tag, and a
 * "Use this hashtag" CTA jumping to the upload screen with the tag prefilled.
 */
const PlyxTagPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    plyxApi
      .get(`/plyx/tag/${encodeURIComponent(slug)}?limit=24`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load hashtag"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/30" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="text-center">
          <Hash className="w-12 h-12 mx-auto text-white/30 mb-3" />
          <div>No hashtag here yet.</div>
          <Link to="/plyx/feed" className="text-sm text-white/55 underline mt-2 inline-block">
            Back to feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="plyx-tag-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 80% 10%, #FFB42D 0%, transparent 40%), radial-gradient(circle at 10% 90%, #FF2D7A 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5 py-10">
          <Link to="/plyx/feed" className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Plyx feed
          </Link>
          <div className="flex items-center gap-3">
            <Hash className="w-10 h-10 text-[#FFB42D]" />
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight" data-testid="plyx-tag-display">
              {data.display}
            </h1>
          </div>
          <div className="mt-4 flex items-center gap-5 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <Play className="w-4 h-4" /> {data.total_videos.toLocaleString()} videos
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Eye className="w-4 h-4" /> {data.total_views.toLocaleString()} views
            </span>
            {data.recent_videos_7d > 0 && (
              <span className="inline-flex items-center gap-1 text-[#FFB42D] font-semibold">
                <Flame className="w-4 h-4" /> {data.recent_videos_7d} this week
              </span>
            )}
          </div>
          <Link
            to={`/plyx/upload?tag=${encodeURIComponent(data.slug)}`}
            data-testid="plyx-tag-use-cta"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-white text-sm"
            style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
          >
            Use this hashtag
          </Link>
        </div>
      </section>

      {/* Grid */}
      <div className="max-w-5xl mx-auto px-5 py-8">
        {data.items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center">
            <Hash className="w-12 h-12 mx-auto text-white/30 mb-3" />
            <div className="font-semibold">No videos yet — be the first.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.items.map((v) => (
              <Link
                key={v.video_id}
                to={`/plyx/v/${v.video_id}`}
                data-testid={`plyx-tag-card-${v.video_id}`}
                className="group block aspect-[9/16] relative rounded-xl overflow-hidden bg-black/60 border border-white/5 hover:border-white/25 transition-colors"
              >
                {v.thumbnail_url ? (
                  <img
                    src={plyxMediaUrl(v.thumbnail_url)}
                    alt={v.caption || ""}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <Play className="w-10 h-10" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 text-[11px] line-clamp-2 leading-tight font-medium">
                  {v.caption || ""}
                </div>
                <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/55 backdrop-blur text-[10px] font-semibold">
                  <Eye className="w-2.5 h-2.5" /> {(v.view_count || 0).toLocaleString()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxTagPage;
